import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { RangeFeature } from "src/app/model/catalog/catalog.model";
import { GlobalStorageService } from "src/app/service-utils/global-storage.service";
import { i18nService } from "src/app/service-utils/i18n.service";
import { ValidatorService } from "src/app/service-utils/validator-service";

@Component({
    selector: 'app-eccentricity-dialog',
    templateUrl: './eccentricity-dialog.component.html',
    styleUrls: ['./eccentricity-dialog.component.scss']
})
export class EccentricityDialogComponent implements OnInit {
    form: FormGroup;

    inputParametersMap = new Map();
    radiusFlach: number;
    radiusSteil: number;
    side: string;
    previousResult = [];
    eccentricities: Array<number> = [];

    constructor(
      public dialogRef: MatDialogRef<EccentricityDialogComponent>,
      @Inject(MAT_DIALOG_DATA) public data: any,
      public i18nService: i18nService,
      private formBuilder: FormBuilder,
      private validatorService: ValidatorService,
      private globalStorageService: GlobalStorageService
    ) {}

    ngOnInit(): void {
        this.initInputParametersMap();
        this.initForm();
    }

    private initForm() {
        this.form = this.formBuilder.group({
            nasal: ["", [this.validatorService.numericValidator('Nasal'), this.validatorService.requiredFieldValidator('Nasal'), this.validatorService.rangeValidator(new RangeFeature('Nasal', true, 5, 15, 0.01))]],
            nasalCalc: [],
            temporal: ["", [this.validatorService.numericValidator('Temporal'), this.validatorService.rangeValidator(new RangeFeature('Temporal', true, 5, 15, 0.01))]],
            temporalCalc: [],
            superior: ["", [this.validatorService.numericValidator('Superior'), this.validatorService.rangeValidator(new RangeFeature('Superior', true, 5, 15, 0.01))]],
            superiorCalc: [],
            inferior: ["", [this.validatorService.numericValidator('Inferior'), this.validatorService.rangeValidator(new RangeFeature('Inferior', true, 5, 15, 0.01))]],
            inferiorCalc: [],
            eccentricityDegree: [this.globalStorageService.degree, [this.validatorService.numericValidator('EccentricityDegree'), this.validatorService.requiredFieldValidator('EccentricityDegree'), this.validatorService.rangeValidator(new RangeFeature('EccentricityDegree', true, 15, 35, 1))]],
        });
        this.initHandlerForm();
        this.restorePreviousResult();
    }

    private initHandlerForm() {
        this.form.get('nasal').valueChanges.subscribe(() => {
            if(this.form.get('eccentricityDegree').value) {
                this.calcFirstEccentricity();
            }
        });

        this.form.get('temporal').valueChanges.subscribe(() => {
            if(+this.form.get('eccentricityDegree').value) {
                this.calcSecondEccentricity();
            }
        });

        this.form.get('superior').valueChanges.subscribe(() => {
            if(+this.form.get('eccentricityDegree').value) {
                this.calcThirdEccentricity();
            }
        });

        this.form.get('inferior').valueChanges.subscribe(() => {
            if(+this.form.get('eccentricityDegree').value) {
                this.calcFourthEccentricity();
            }
        });

        this.form.get('eccentricityDegree').valueChanges.subscribe(() => {
            this.caclAllEccentricity();
        });
    }

    private restorePreviousResult() {
        if(this.data.previousEccentricityDialogResult && Object.keys(this.data.previousEccentricityDialogResult).length > 0) {
            this.form.patchValue({
                'nasal': this.data.previousEccentricityDialogResult.nasal,
                'temporal': this.data.previousEccentricityDialogResult.temporal,
                'superior': this.data.previousEccentricityDialogResult.superior,
                'inferior': this.data.previousEccentricityDialogResult.inferior,
                'eccentricityDegree': this.data.previousEccentricityDialogResult.eccentricityDegree,
            });
            this.caclAllEccentricity();
        }
    }

    private initInputParametersMap() {
        this.previousResult = this.data.previousEccentricityDialogResult;

        this.radiusFlach = this.data.radiusFlach;
        this.radiusSteil = this.data.radiusSteil;

        this.inputParametersMap.set("radiusFlach", this.i18nService.replaceFromDotToComma(this.radiusFlach));
        this.inputParametersMap.set("radiusFlachDegree", this.i18nService.replaceFromDotToComma(this.data.radiusFlachDegree));
        this.inputParametersMap.set("radiusSteil", this.i18nService.replaceFromDotToComma(this.radiusSteil));
        this.inputParametersMap.set("radiusSteilDegree", this.i18nService.replaceFromDotToComma(this.data.radiusSteilDegree));
        this.side = this.data.side;
    }

    private caclAllEccentricity() {
        this.calcFirstEccentricity();
        this.calcSecondEccentricity();
        this.calcThirdEccentricity();
        this.calcFourthEccentricity();
    }

    private calcFirstEccentricity() {
        let nasal = this.i18nService.replaceFromCommaToDot(this.form.get('nasal').value);

        if(this.radiusFlach > 0 && nasal > 0) {
            let degree = this.i18nService.replaceFromCommaToDot(this.form.get('eccentricityDegree').value);
            let rk = nasal + (this.radiusFlach - this.radiusSteil);
            this.eccentricities[0] = this.calcEccentricity(this.radiusFlach, rk, degree);
        } else {
            this.eccentricities[0] = 0;
        }

        this.form.patchValue({'nasalCalc': this.i18nService.replaceFromDotToComma(this.eccentricities[0])});
    }

    private calcSecondEccentricity() {
        let temporal = this.i18nService.replaceFromCommaToDot(this.form.get('temporal').value);

        if(this.radiusFlach > 0 && temporal > 0) {
            let degree = this.i18nService.replaceFromCommaToDot(this.form.get('eccentricityDegree').value);
            let rk = temporal + (this.radiusFlach - this.radiusSteil);
            this.eccentricities[1] = this.calcEccentricity(this.radiusFlach, rk, degree);
        } else {
            this.eccentricities[1] = 0;
        }
        this.form.patchValue({'temporalCalc': this.i18nService.replaceFromDotToComma(this.eccentricities[1])});
    }

    private calcThirdEccentricity() {
        let superior = this.i18nService.replaceFromCommaToDot(this.form.get('superior').value);

        if(this.radiusSteil > 0 && superior > 0) {
            let degree = this.i18nService.replaceFromCommaToDot(this.form.get('eccentricityDegree').value);
            let rk = superior + ((-1) * (this.radiusFlach - this.radiusSteil));
            this.eccentricities[2] = this.calcEccentricity(this.radiusSteil, rk, degree);
        } else {
            this.eccentricities[2] = 0;
        }
        this.form.patchValue({'superiorCalc': this.i18nService.replaceFromDotToComma(this.eccentricities[2])});
    }

    private calcFourthEccentricity() {
        let inferior = this.i18nService.replaceFromCommaToDot(this.form.get('inferior').value);

        if(this.radiusSteil > 0 && inferior > 0) {
            let degree = this.i18nService.replaceFromCommaToDot(this.form.get('eccentricityDegree').value);
            let rk = inferior + ((-1) * (this.radiusFlach - this.radiusSteil));
            this.eccentricities[3] = this.calcEccentricity(this.radiusSteil, rk, degree);
        } else {
            this.eccentricities[3] = 0;
        }
        this.form.patchValue({'inferiorCalc': this.i18nService.replaceFromDotToComma(this.eccentricities[3])});
    }

    private calcEccentricity(radius: number, sagittal: number, degree: number): number {
        if(radius > sagittal) {
            return +((1 / Math.sin(degree * (Math.PI/180))) * (-1) * Math.sqrt(Math.abs(1 - Math.pow(radius/sagittal, 2)))).toFixed(2);
        } else if(radius === sagittal) {
            return 0;
        } else {
            return +((1 / Math.sin(degree * (Math.PI/180))) * Math.sqrt(1 - Math.pow(radius/sagittal, 2))).toFixed(2);
        }
    }

    calcFinalEccentricity() {
        this.checkRequiredFields();

        if(this.form.status === "VALID") {
            let minus = this.eccentricities.filter(eccentricity => eccentricity < 0).length;
            let finalEccentricities = this.eccentricities;

            if(minus <= 2) {
                finalEccentricities = this.eccentricities.map(eccentricity => eccentricity < 0 ? eccentricity = 0 : eccentricity)
            }
            let finalResult = (finalEccentricities.reduce((accumulator, currentValue) => accumulator + currentValue) / 4).toFixed(2);
            this.dialogRef.close({"finalResult": +finalResult,
                                  "nasal": this.form.get("nasal").value,
                                  "superior": this.form.get("superior").value,
                                  "temporal": this.form.get("temporal").value,
                                  "inferior": this.form.get("inferior").value,
                                  "eccentricityDegree": this.form.get("eccentricityDegree").value});
        }
    }

    checkRequiredFields() {

        if(this.form.get('nasal').value === "") {
          this.form.get('nasal').markAsTouched();
          this.form.get('nasal').setValue("");
        }
  
        if(this.form.get('superior').value === "") {
          this.form.get('superior').markAsTouched();
          this.form.get('superior').setValue("");
        }
  
        if(this.form.get('temporal').value === "") {
          this.form.get('temporal').markAsTouched();
          this.form.get('temporal').setValue("");
        }

        if(this.form.get('inferior').value === "") {
          this.form.get('inferior').markAsTouched();
          this.form.get('inferior').setValue("");
        }

        if(this.form.get('eccentricityDegree').value === "") {
          this.form.get('eccentricityDegree').markAsTouched();
          this.form.get('eccentricityDegree').setValue("");
        }
      }

    discard() {
        this.dialogRef.close();
    }

    getLimitsFormatted(rangeMin: number, rangeMax: number) {
        return {
          rangeMin: rangeMin,
          rangeMax: rangeMax
        }
    }

    setValueWithFormat(featureName: string, control: string) {
        this.form.controls[control].setValue(
            this.i18nService.replaceFromDotToComma(
                this.i18nService.formatIfNumber(this.form.controls[control].value, this.globalStorageService.decimalFormats.find(feature => feature.featureName === featureName)?.decimalFormat)
            )
        );
    }
}